import React, { useState, useEffect } from 'react'
import {
  Box,
  Paper,
  Typography,
  Grid,
  Button,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  Divider,
  Chip,
  InputAdornment,
  IconButton,
  Avatar,
  Container,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Card,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  DialogContentText,
  Tabs,
  Tab,
  InputBase,
  alpha,
  styled,
} from '@mui/material'
import {
  Search as SearchIcon,
  FilterList as FilterIcon,
  Download as DownloadIcon,
  Refresh as RefreshIcon,
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  AccessTime as PendingIcon,
  ArrowBack as BackIcon,
  Visibility as ViewIcon,
} from '@mui/icons-material'
import { useNavigate } from 'react-router-dom'
import { useSelector } from 'react-redux'
import { DatePicker } from '@mui/x-date-pickers/DatePicker'
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider'
import { format, parse } from 'date-fns'
import toast from 'react-hot-toast'
import { usePagination, useDialog, useLoading } from '../../hooks'
import { StatusChip } from '../../components/common/Chips'
import { formatDate } from '../../utils/formatters'
import { leaveApi } from '../../api/leaveApi'

import { exportToCSV } from '../../utils/exportUtils'
import TableSkeleton from '../../components/common/Skeleton/TableSkeleton'

const Search = styled('div')(({ theme }) => ({
  position: 'relative',
  borderRadius: theme.shape.borderRadius,
  backgroundColor: alpha(theme.palette.common.black, 0.05),
  '&:hover': {
    backgroundColor: alpha(theme.palette.common.black, 0.1),
  },
  marginRight: 0,
  marginLeft: 0,
  width: '100%',
}))

const SearchIconWrapper = styled('div')(({ theme }) => ({
  padding: theme.spacing(0, 2),
  height: '100%',
  position: 'absolute',
  pointerEvents: 'none',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: theme.palette.text.secondary,
}))

const StyledInputBase = styled(InputBase)(({ theme }) => ({
  color: 'inherit',
  width: '100%',
  '& .MuiInputBase-input': {
    padding: theme.spacing(1, 1, 1, 0),
    paddingLeft: `calc(1em + ${theme.spacing(4)})`,
    transition: theme.transitions.create('width'),
    width: '100%',
  },
}))



const EmployeeLeaveManagement = () => {
  const navigate = useNavigate()
  const { user } = useSelector((state) => state.auth)
  const { loading: actionLoading, startLoading, stopLoading } = useLoading()

  const approveDialog = useDialog()
  const rejectDialog = useDialog()
  const viewDialog = useDialog()
  const pagination = usePagination(10)

  const [selectedTab, setSelectedTab] = useState(0)
  const [searchTerm, setSearchTerm] = useState('')
  const [filterStatus, setFilterStatus] = useState('all')
  const [approvalRemark, setApprovalRemark] = useState('')
  const [rejectionRemark, setRejectionRemark] = useState('')
  const [filterDate, setFilterDate] = useState({ from: null, to: null })
  const [filterDepartment, setFilterDepartment] = useState('all')
  const [stats, setStats] = useState({ total: 0, pending: 0, approved: 0, rejected: 0 })
  const [totalPages, setTotalPages] = useState(0)

  // Leave data state
  const [leaveRequests, setLeaveRequests] = useState([])

  const getLeaveIcon = (type) => {
    const lower = type?.toLowerCase() || ''
    if (lower.includes('casual')) return '🏖️'
    if (lower.includes('sick')) return '🏥'
    if (lower.includes('earned') || lower.includes('paid')) return '💰'
    if (lower.includes('maternity') || lower.includes('paternity')) return '👶'
    if (lower.includes('work from home')) return '🏠'
    return '📅'
  }



  const fetchLeaves = async () => {
    try {
      startLoading()

      // Calculate current filter status based on tab and dropdown
      let currentFilterStatus = null
      if (tabs[selectedTab].value !== 'all') {
        currentFilterStatus = tabs[selectedTab].value
      } else if (filterStatus !== 'all') {
        currentFilterStatus = filterStatus
      }

      const params = {
        page: pagination.page + 1,
        limit: pagination.rowsPerPage,
        status_filter: currentFilterStatus,
        from_date: filterDate.from ? format(filterDate.from, 'yyyy-MM-dd') : null,
        to_date: filterDate.to ? format(filterDate.to, 'yyyy-MM-dd') : null,
      }

      const response = await leaveApi.getAllLeaves(params)
      if (response.data && response.data.data) {
        const mappedData = response.data.data.map(leave => ({
          id: leave.id,
          employeeId: leave.employee_code || leave.employee_id,
          employeeName: leave.employee_name,
          employeeAvatar: leave.employee_name ? leave.employee_name.charAt(0) : '?',
          department: leave.department,
          designation: leave.designation,
          leaveType: leave.leave_type.charAt(0).toUpperCase() + leave.leave_type.slice(1),
          leaveTypeIcon: getLeaveIcon(leave.leave_type),
          fromDate: leave.start_date,
          toDate: leave.end_date,
          duration: leave.duration,
          appliedOn: leave.applied_on,
          status: leave.status,
          reason: leave.reason,
          emergencyContact: leave.emergency_contact_number,
          handoverTo: leave.work_handover_to_name || 'N/A',
          workHandoverNotes: leave.work_handover_notes || 'N/A',
          balanceBefore: leave.leave_balance?.total || 0,
          balanceAfter: leave.leave_balance?.remaining || 0,
          approvedBy: leave.status === 'approved' ? leave.approved_by_name : null,
          approvedOn: leave.status === 'approved' ? formatDate(leave.approved_at) : null,
          rejectedBy: leave.status === 'rejected' ? leave.approved_by_name : null,
          rejectedOn: leave.status === 'rejected' ? formatDate(leave.approved_at) : null,
          approvalRemark: '',
          rejectionRemark: '',
        }))
        setLeaveRequests(mappedData)
        setStats({
          total: response.data.total || 0,
          pending: response.data.stats?.pending || 0,
          approved: response.data.stats?.approved || 0,
          rejected: response.data.stats?.rejected || 0
        })
        setTotalPages(response.data.total_pages || 0)
      }
    } catch (error) {
      console.error('Error fetching leaves:', error)
      toast.error('Failed to fetch leave requests')
    } finally {
      stopLoading()
    }
  }

  // Effect to fetch data when filters or pagination change
  useEffect(() => {
    fetchLeaves()
  }, [pagination.page, pagination.rowsPerPage, filterStatus, filterDate, selectedTab])

  // Departments for filter
  const departments = [
    'Engineering',
    'Design',
    'QA',
    'Marketing',
    'Sales',
    'HR',
    'Finance',
    'Operations',
  ]

  const statusOptions = [
    { value: 'all', label: 'All Status', color: 'default' },
    { value: 'pending', label: 'Pending', color: 'warning' },
    { value: 'approved', label: 'Approved', color: 'success' },
    { value: 'rejected', label: 'Rejected', color: 'error' },
    { value: 'cancelled', label: 'Cancelled', color: 'default' },
  ]

  const tabs = [
    { label: 'All Leaves', value: 'all', count: stats.total },
    { label: 'Pending', value: 'pending', count: stats.pending },
    { label: 'Approved', value: 'approved', count: stats.approved },
    { label: 'Rejected', value: 'rejected', count: stats.rejected },
  ]

  const handleTabChange = (event, newValue) => {
    setSelectedTab(newValue)
    pagination.setPage(0)
  }

  const handleSearch = (event) => {
    setSearchTerm(event.target.value)
    pagination.setPage(0)
  }

  const handleFilterStatus = (event) => {
    setFilterStatus(event.target.value)
    pagination.setPage(0)
  }

  const handleFilterDepartment = (event) => {
    setFilterDepartment(event.target.value)
    pagination.setPage(0)
  }

  const handleDateFilter = (date, type) => {
    setFilterDate(prev => ({ ...prev, [type]: date }))
    pagination.setPage(0)
  }

  const handleApproveConfirm = async () => {
    if (!approveDialog.data) return

    startLoading()
    try {
      await leaveApi.updateLeaveStatus(approveDialog.data.id, 'approved', approvalRemark || 'Leave approved.')
      approveDialog.closeDialog()
      setApprovalRemark('')
      fetchLeaves()
    } catch (error) {
      console.error(error)
    } finally {
      stopLoading()
    }
  }

  const handleRejectConfirm = async () => {
    if (!rejectDialog.data) {
      toast.error('Please select a leave to reject')
      return
    }

    startLoading()
    try {
      await leaveApi.updateLeaveStatus(rejectDialog.data.id, 'rejected', rejectionRemark || 'Leave rejected.')

      rejectDialog.closeDialog()
      setRejectionRemark('')
      fetchLeaves() // Refresh list to get updated data from server
    } catch (error) {
      console.error(error)
    } finally {
      stopLoading()
    }
  }

  const handleExportData = () => {
    const columns = [
      { label: 'Employee ID', key: 'employeeId' },
      { label: 'Employee Name', key: 'employeeName' },
      { label: 'Department', key: 'department' },
      { label: 'Designation', key: 'designation' },
      { label: 'Leave Type', key: 'leaveType' },
      { label: 'From Date', key: 'fromDate' },
      { label: 'To Date', key: 'toDate' },
      { label: 'Duration (Days)', key: 'duration' },
      { label: 'Reason', key: 'reason' },
      { label: 'Status', key: 'status' },
      { label: 'Applied On', key: 'appliedOn' },
      { label: 'Approved/Rejected By', key: (row) => row.approvedBy || row.rejectedBy || '-' },
      { label: 'Action Date', key: (row) => row.approvedOn || row.rejectedOn || '-' }
    ]

    exportToCSV(filteredLeaves, columns, 'leave-requests')
    toast.success('Exporting leave data...')
  }

  const handleResetFilters = () => {
    setSearchTerm('')
    setFilterStatus('all')
    setFilterDepartment('all')
    setFilterDate({ from: null, to: null })
    pagination.setPage(0)
  }

  // Filter leaves locally for search term and department since API might not support them yet
  const filteredLeaves = leaveRequests.filter(leave => {
    const matchesSearch =
      leave.employeeName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      leave.employeeId?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      leave.department?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      leave.leaveType?.toLowerCase().includes(searchTerm.toLowerCase())

    const matchesDepartment =
      filterDepartment === 'all' ||
      leave.department === filterDepartment

    return matchesSearch && matchesDepartment
  })

  return (
    <Box sx={{ p: { xs: 1, md: 3 } }}>
      <Paper sx={{ p: { xs: 2, md: 3 }, borderRadius: 3, bgcolor: 'background.paper', boxShadow: 3 }}>
        {/* Header */}
        <Box sx={{ mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Box sx={{ flexGrow: 1 }}>
              <Typography variant="h4" fontWeight="bold" gutterBottom>
                Employee Leave Management
              </Typography>
              <Typography variant="body2" color="textSecondary">
                Review, approve, or reject employee leave requests
              </Typography>
            </Box>
          </Box>

          {/* Stats Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined" sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="primary">
                  {stats.total}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Total Requests
                </Typography>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined" sx={{ p: 2, textAlign: 'center', borderLeft: '4px solid #ff9800' }}>
                <Typography variant="h4" color="warning.main">
                  {stats.pending}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Pending Approval
                </Typography>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined" sx={{ p: 2, textAlign: 'center', borderLeft: '4px solid #4caf50' }}>
                <Typography variant="h4" color="success.main">
                  {stats.approved}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Approved
                </Typography>
              </Card>
            </Grid>
            <Grid size={{ xs: 12, sm: 6, md: 3 }}>
              <Card variant="outlined" sx={{ p: 2, textAlign: 'center', borderLeft: '4px solid #f44336' }}>
                <Typography variant="h4" color="error.main">
                  {stats.rejected}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Rejected
                </Typography>
              </Card>
            </Grid>
          </Grid>

          {/* Tabs with better styling */}
          <Paper variant="outlined" sx={{ mb: 3 }}>
            <Tabs
              value={selectedTab}
              onChange={handleTabChange}
              variant="scrollable"
              scrollButtons="auto"
              sx={{
                '& .MuiTab-root': {
                  minHeight: 48,
                  position: 'relative',
                }
              }}
            >
              {tabs.map((tab, index) => (
                <Tab
                  key={tab.value}
                  label={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <span>{tab.label}</span>
                      <Chip
                        label={tab.count}
                        size="small"
                        color={
                          tab.value === 'pending' ? 'warning' :
                            tab.value === 'approved' ? 'success' :
                              tab.value === 'rejected' ? 'error' : 'primary'
                        }
                        sx={{
                          height: 20,
                          fontSize: '0.75rem',
                          minWidth: 20,
                          '& .MuiChip-label': { px: 1 }
                        }}
                      />
                    </Box>
                  }
                />
              ))}
            </Tabs>
          </Paper>
        </Box>

        {/* Filters Section */}
        <Paper variant="outlined" sx={{ p: 2, mb: 3 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid size={{ xs: 12, md: 2 }}>
              <Search>
                <SearchIconWrapper>
                  <SearchIcon />
                </SearchIconWrapper>
                <StyledInputBase
                  placeholder="Search..."
                  value={searchTerm}
                  onChange={handleSearch}
                />
              </Search>
            </Grid>

            <Grid size={{ xs: 12, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Status</InputLabel>
                <Select
                  value={filterStatus}
                  label="Status"
                  onChange={handleFilterStatus}
                >
                  {statusOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 2 }}>
              <FormControl fullWidth size="small">
                <InputLabel>Department</InputLabel>
                <Select
                  value={filterDepartment}
                  label="Department"
                  onChange={handleFilterDepartment}
                >
                  <MenuItem value="all">All Departments</MenuItem>
                  {departments.map(dept => (
                    <MenuItem key={dept} value={dept}>
                      {dept}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid size={{ xs: 12, md: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="From Date"
                  value={filterDate.from}
                  onChange={(newValue) => handleDateFilter(newValue, 'from')}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid size={{ xs: 12, md: 2 }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="To Date"
                  value={filterDate.to}
                  onChange={(newValue) => handleDateFilter(newValue, 'to')}
                  minDate={filterDate.from}
                  slotProps={{ textField: { fullWidth: true, size: 'small' } }}
                />
              </LocalizationProvider>
            </Grid>

            <Grid size={{ xs: 12, md: 2 }}>
              <Button
                variant="outlined"
                startIcon={<RefreshIcon />}
                onClick={handleResetFilters}
                fullWidth
              >
                Reset
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {/* Leaves Table */}
        <Paper variant="outlined" sx={{ width: '100%', overflow: 'hidden' }}>
          <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', borderBottom: 1, borderColor: 'divider' }}>
            <Button
              variant="contained"
              startIcon={<DownloadIcon />}
              onClick={handleExportData}
            >
              Export Data
            </Button>
          </Box>
          <TableContainer sx={{ maxHeight: 600, overflowX: 'auto' }}>
            <Table stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell>Employee</TableCell>
                  <TableCell>Leave Details</TableCell>
                  <TableCell>Duration</TableCell>
                  <TableCell>Applied On</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {actionLoading ? (
                  <TableSkeleton columns={6} rows={pagination.rowsPerPage} avatar />
                ) : filteredLeaves.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={6} align="center" sx={{ py: 3 }}>
                      <Typography variant="body1" color="textSecondary">
                        No leave requests found
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  filteredLeaves.map((leave) => (
                    <TableRow
                      key={leave.id}
                      hover
                      sx={{
                        backgroundColor: (theme) =>
                          leave.status === 'pending'
                            ? alpha(theme.palette.warning.main, theme.palette.mode === 'light' ? 0.1 : 0.2)
                            : 'inherit',
                        '&:hover': {
                          backgroundColor: (theme) =>
                            leave.status === 'pending'
                              ? alpha(theme.palette.warning.main, theme.palette.mode === 'light' ? 0.2 : 0.3)
                              : theme.palette.action.hover,
                        },
                      }}
                    >
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                          <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                            {leave.employeeAvatar}
                          </Avatar>
                          <Box>
                            <Typography variant="body2" fontWeight="bold">
                              {leave.employeeName}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {leave.employeeId} • {leave.designation}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Box>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                            <Typography variant="body2">
                              {leave.leaveTypeIcon} {leave.leaveType}
                            </Typography>
                          </Box>
                          <Typography variant="caption" color="textSecondary">
                            {formatDate(leave.fromDate)} - {formatDate(leave.toDate)}
                          </Typography>
                          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                            {leave.reason.length > 50 ? `${leave.reason.substring(0, 50)}...` : leave.reason}
                          </Typography>
                        </Box>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2" fontWeight="bold">
                          {leave.duration} day{leave.duration > 1 ? 's' : ''}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          Balance: {leave.balanceBefore} → {leave.balanceAfter}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <Typography variant="body2">
                          {formatDate(leave.appliedOn)}
                        </Typography>
                        <Typography variant="caption" color="textSecondary">
                          {(() => {
                            const date = parse(leave.appliedOn, 'dd-MM-yyyy', new Date())
                            const now = new Date()
                            const diffMs = now - date
                            const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24))

                            if (diffDays > 30) {
                              const months = Math.floor(diffDays / 30)
                              return `${months} month${months > 1 ? 's' : ''} ago`
                            } else if (diffDays > 7) {
                              const weeks = Math.floor(diffDays / 7)
                              return `${weeks} week${weeks > 1 ? 's' : ''} ago`
                            } else if (diffDays > 0) {
                              return `${diffDays} day${diffDays > 1 ? 's' : ''} ago`
                            } else {
                              return 'Today'
                            }
                          })()}
                        </Typography>
                      </TableCell>

                      <TableCell>
                        <StatusChip status={leave.status} />
                        {leave.approvedBy && (
                          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                            By: {leave.approvedBy}
                          </Typography>
                        )}
                        {leave.rejectedBy && (
                          <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                            By: {leave.rejectedBy}
                          </Typography>
                        )}
                      </TableCell>

                      <TableCell>
                        <Box sx={{ display: 'flex', gap: 1 }}>
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={async () => {
                              try {
                                startLoading()
                                const response = await leaveApi.getLeaveDetails(leave.id)
                                if (response.data) {
                                  // Map API details to flat structure matching component state
                                  const apiData = response.data
                                  const detailedLeave = {
                                    ...leave, // Keep existing data (like appliedOn)
                                    id: apiData.id,
                                    employeeName: apiData.employee_info?.employee_name,
                                    employeeId: apiData.employee_info?.employee_code,
                                    department: apiData.employee_info?.department,
                                    designation: apiData.employee_info?.designation,
                                    leaveType: apiData.leave_info?.leave_type,
                                    fromDate: apiData.leave_info?.from_date,
                                    toDate: apiData.leave_info?.to_date,
                                    duration: apiData.leave_info?.days_requested, // Use days_requested for standard duration
                                    durationText: apiData.leave_info?.duration, // 'full day' etc
                                    reason: apiData.additional_info?.reason,
                                    emergencyContact: apiData.additional_info?.emergency_contact_number,
                                    handoverTo: apiData.additional_info?.work_handover_to_name || 'N/A',
                                    workHandoverNotes: apiData.additional_info?.work_handover_notes || 'N/A',
                                    leaveBalance: apiData.leave_info?.leave_balance,
                                    status: apiData.other_info?.status,
                                    approvedBy: apiData.other_info?.approved_by_name,
                                    approvedOn: apiData.other_info?.approved_at,
                                    rejectedBy: apiData.other_info?.approved_by_name,
                                    rejectedOn: apiData.other_info?.approved_at,
                                    approvalRemark: apiData.other_info?.remarks, // Assuming remarks maps here
                                    rejectionRemark: apiData.other_info?.remarks
                                  }
                                  viewDialog.openDialog(detailedLeave)
                                }
                              } catch (error) {
                                console.error('Failed to fetch leave details:', error)
                                toast.error('Failed to fetch leave details')
                              } finally {
                                stopLoading()
                              }
                            }}
                            title="View Details"
                          >
                            <ViewIcon fontSize="small" />
                          </IconButton>

                          {leave.status === 'pending' && (
                            <>
                              <IconButton
                                size="small"
                                color="success"
                                onClick={() => approveDialog.openDialog(leave)}
                                title="Approve"
                              >
                                <ApproveIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                size="small"
                                color="error"
                                onClick={() => rejectDialog.openDialog(leave)}
                                title="Reject"
                              >
                                <RejectIcon fontSize="small" />
                              </IconButton>
                            </>
                          )}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={stats.total} // Use total from API for pagination
            rowsPerPage={pagination.rowsPerPage}
            page={pagination.page}
            onPageChange={pagination.handleChangePage}
            onRowsPerPageChange={pagination.handleChangeRowsPerPage}
          />
        </Paper>
      </Paper>

      {/* View Details Dialog */}
      <Dialog
        open={viewDialog.isOpen}
        onClose={viewDialog.closeDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <Typography variant="h6">Leave Request Details</Typography>
            {viewDialog.data && <StatusChip status={viewDialog.data.status} />}
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          {viewDialog.data && (
            <Grid container spacing={3}>
              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="textSecondary">Employee</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mt: 1 }}>
                    <Avatar sx={{ bgcolor: 'primary.main' }}>
                      {viewDialog.data.employeeName?.charAt(0) || '?'}
                    </Avatar>
                    <Box>
                      <Typography variant="body1" fontWeight="bold">
                        {viewDialog.data.employeeName}
                      </Typography>
                      <Typography variant="body2" color="textSecondary">
                        {viewDialog.data.employeeId} • {viewDialog.data.designation}
                      </Typography>
                      <Typography variant="caption" color="textSecondary" display="block">
                        {viewDialog.data.department}
                      </Typography>
                    </Box>
                  </Box>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="textSecondary">Leave Details</Typography>
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="textSecondary">Type</Typography>
                      <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        {getLeaveIcon(viewDialog.data.leaveType)} {viewDialog.data.leaveType}
                      </Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="textSecondary">Duration</Typography>
                      <Typography variant="body2">{viewDialog.data.duration} day(s) ({viewDialog.data.durationText})</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="textSecondary">From</Typography>
                      <Typography variant="body2">{viewDialog.data.fromDate}</Typography>
                    </Grid>
                    <Grid size={{ xs: 6 }}>
                      <Typography variant="caption" color="textSecondary">To</Typography>
                      <Typography variant="body2">{viewDialog.data.toDate}</Typography>
                    </Grid>
                    <Grid size={{ xs: 12 }}>
                      <Typography variant="caption" color="textSecondary">Reason</Typography>
                      <Typography variant="body2">{viewDialog.data.reason}</Typography>
                    </Grid>
                  </Grid>
                </Box>
              </Grid>

              <Grid size={{ xs: 12, md: 6 }}>
                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="textSecondary">Balance</Typography>
                  <Grid container spacing={2} sx={{ mt: 0.5 }}>
                    <Grid size={{ xs: 4 }}>
                      <Typography variant="caption" color="textSecondary">Total</Typography>
                      <Typography variant="body2">{viewDialog.data.leaveBalance?.total || 0}</Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <Typography variant="caption" color="textSecondary">Applied</Typography>
                      <Typography variant="body2">{viewDialog.data.leaveBalance?.applied_for || 0}</Typography>
                    </Grid>
                    <Grid size={{ xs: 4 }}>
                      <Typography variant="caption" color="textSecondary">Remaining</Typography>
                      <Typography variant="body2">{viewDialog.data.leaveBalance?.remaining || 0}</Typography>
                    </Grid>
                  </Grid>
                </Box>

                <Box sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" color="textSecondary">Additional Information</Typography>
                  <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Applied On:</Typography>
                      <Typography variant="body2" fontWeight="medium">{viewDialog.data.appliedOn}</Typography>
                    </Box>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Emergency Contact:</Typography>
                      <Typography variant="body2" fontWeight="medium">{viewDialog.data.emergencyContact}</Typography>
                    </Box>
                    <Divider />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="body2">Work Handover:</Typography>
                      <Typography variant="body2" fontWeight="medium">{viewDialog.data.handoverTo}</Typography>
                    </Box>
                  </Box>
                </Box>

                {viewDialog.data.status === 'approved' && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="success.main">Approval Details</Typography>
                    <Paper variant="outlined" sx={{ p: 2, mt: 0.5, bgcolor: '#e8f5e9', borderColor: '#a5d6a7' }}>
                      <Typography variant="body2">
                        <strong>Approved By:</strong> {viewDialog.data.approvedBy}
                      </Typography>
                      {viewDialog.data.approvedOn && (
                        <Typography variant="caption" color="textSecondary">
                          On: {formatDate(viewDialog.data.approvedOn, 'dd/MM/yyyy')}
                        </Typography>
                      )}
                      {viewDialog.data.approvalRemark && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Remark:</strong> {viewDialog.data.approvalRemark}
                        </Typography>
                      )}
                    </Paper>
                  </Box>
                )}

                {viewDialog.data.status === 'rejected' && (
                  <Box sx={{ mb: 3 }}>
                    <Typography variant="subtitle2" color="error.main">Rejection Details</Typography>
                    <Paper variant="outlined" sx={{ p: 2, mt: 0.5, bgcolor: '#ffebee', borderColor: '#ef9a9a' }}>
                      <Typography variant="body2">
                        <strong>Rejected By:</strong> {viewDialog.data.rejectedBy}
                      </Typography>
                      {viewDialog.data.rejectedOn && (
                        <Typography variant="caption" color="textSecondary">
                          On: {formatDate(viewDialog.data.rejectedOn, 'dd/MM/yyyy')}
                        </Typography>
                      )}
                      {viewDialog.data.rejectionRemark && (
                        <Typography variant="body2" sx={{ mt: 1 }}>
                          <strong>Reason:</strong> {viewDialog.data.rejectionRemark}
                        </Typography>
                      )}
                    </Paper>
                  </Box>
                )}

                {viewDialog.data.status === 'pending' && (
                  <Box>
                    <Typography variant="subtitle2" color="textSecondary" sx={{ mb: 1 }}>Actions</Typography>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Button
                        variant="contained"
                        color="success"
                        startIcon={<ApproveIcon />}
                        fullWidth
                        onClick={() => {
                          viewDialog.closeDialog()
                          approveDialog.openDialog(viewDialog.data)
                        }}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="contained"
                        color="error"
                        startIcon={<RejectIcon />}
                        fullWidth
                        onClick={() => {
                          viewDialog.closeDialog()
                          rejectDialog.openDialog(viewDialog.data)
                        }}
                      >
                        Reject
                      </Button>
                    </Box>
                  </Box>
                )}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={viewDialog.closeDialog}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Approval Dialog */}
      <Dialog
        open={approveDialog.isOpen}
        onClose={approveDialog.closeDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Approve Leave Request</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to approve the leave request for <strong>{approveDialog.data?.employeeName}</strong>?
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Approval Remarks (Optional)"
            fullWidth
            multiline
            rows={3}
            value={approvalRemark}
            onChange={(e) => setApprovalRemark(e.target.value)}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={approveDialog.closeDialog}>Cancel</Button>
          <Button
            onClick={handleApproveConfirm}
            color="success"
            variant="contained"
            disabled={actionLoading}
          >
            {actionLoading ? 'Approving...' : 'Confirm Approve'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Rejection Dialog */}
      <Dialog
        open={rejectDialog.isOpen}
        onClose={rejectDialog.closeDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Reject Leave Request</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 2 }}>
            Are you sure you want to reject the leave request for <strong>{rejectDialog.data?.employeeName}</strong>?
          </DialogContentText>
          <TextField
            autoFocus
            margin="dense"
            label="Reason for Rejection"
            fullWidth
            multiline
            rows={3}
            value={rejectionRemark}
            onChange={(e) => setRejectionRemark(e.target.value)}
            required
            error={rejectionRemark.trim() === ''}
            helperText={rejectionRemark.trim() === '' ? "Rejection reason is required" : ""}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={rejectDialog.closeDialog}>Cancel</Button>
          <Button
            onClick={handleRejectConfirm}
            color="error"
            variant="contained"
            disabled={actionLoading || rejectionRemark.trim() === ''}
          >
            {actionLoading ? 'Rejecting...' : 'Confirm Reject'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  )
}

export default EmployeeLeaveManagement